import { Component, EventEmitter, inject, Input, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { ErrorService } from '@portal-core/errors/services/error.service';
import { License } from '@portal-core/licenses/models/license.model';
import { LicensesService } from '@portal-core/licenses/services/licenses.service';
import { AutoUnsubscribe } from '@portal-core/util/auto-unsubscribe.decorator';
import { LoadingState } from '@portal-core/util/loading-state';
import { Observable, Subscription, catchError, map, of } from 'rxjs';
import { FormComponent } from '@portal-core/general/components/form/form.component';
import { FormBodyDirective } from '@portal-core/general/directives/form/form-body/form-body.directive';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { McColorDirective } from '@portal-core/ui/themes/directives/mc-color.directive';
import { MatInput } from '@angular/material/input';
import { FormControlErrorMessageComponent } from '@portal-core/forms/components/form-control-error-message/form-control-error-message.component';
import { MatIcon } from '@angular/material/icon';
import { MatSelect, MatOption } from '@angular/material/select';
import { NgFor } from '@angular/common';

interface LicenseOpenAIForm {
  key: FormControl<string>,
  version: FormControl<string>
}

interface ModelOption {
  text: string,
  value: string
}

@Component({
    selector: 'mc-license-open-ai-form',
    templateUrl: './license-open-ai-form.component.html',
    styleUrls: ['./license-open-ai-form.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [FormComponent, ReactiveFormsModule, FormBodyDirective, MatFormField, McColorDirective, MatLabel, MatInput, MatError, FormControlErrorMessageComponent, MatIcon, MatSuffix, MatSelect, NgFor, MatOption]
})
@AutoUnsubscribe()
export class LicenseOpenAIFormComponent implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly licensesService = inject(LicensesService);
  private readonly errorService = inject(ErrorService);

  @Input() license: License;
  @Output() canceled = new EventEmitter<void>();

  hide: boolean = true;
  loadingState: LoadingState<string> = new LoadingState<string>();

  AIForm: FormGroup<LicenseOpenAIForm>;

  models: ModelOption[] = [
    { text: 'GPT-5', value: 'gpt-5' },
    { text: 'GPT-5 mini', value: 'gpt-5-mini' },
    { text: 'GPT-4o', value: 'gpt-4o' },
    { text: 'GPT-4', value: 'gpt-4' },
    { text: 'GPT-3.5', value: 'gpt-3.5-turbo' }
  ];

  versionSubscription: Subscription;
  settingsSubscription: Subscription;
  ngOnInit() {
    this.buildForm();
    this.versionSubscription = this.AIForm.controls.version.valueChanges.subscribe(() => this.AIForm.controls.key.updateValueAndValidity());

    this.loadingState.update(true);

    // The key is not included on the license model; fetch it from the ServerManagement-gated endpoint to pre-populate the form.
    this.settingsSubscription = this.licensesService.getOpenAISettings$(this.license.Id).subscribe(settings => {
      this.AIForm.patchValue({
        key: settings.ApiKey ?? '',
        version: settings.ApiModel ? settings.ApiModel : 'gpt-4o'
      });
      this.loadingState.update(false);
    }, error => {
      this.loadingState.update(false, 'There was a problem loading the OpenAI settings.', this.errorService.getErrorMessages(error));
    });
  }

  submitFn = (): Observable<MadCloudResult<void>> => {
    return this.licensesService.changeOpenAISettings$(this.license.Id, this.AIForm.value.key, this.AIForm.value.version);
  }

  private buildForm() {
    this.AIForm = this.formBuilder.group<LicenseOpenAIForm>({
      key: new FormControl('', { asyncValidators: [this.createValidateOpenAI()] }),
      version: new FormControl('gpt-4o')
    });
  }

  private createValidateOpenAI(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors> => {
      const [apiKey, apiModel] = [
        control?.value,
        this.AIForm?.controls?.version?.value
      ];
      if (apiKey && apiModel) {
        return this.licensesService.verifyOpenAIKey$(apiKey, apiModel).pipe(
          map((): ValidationErrors => null),
          catchError(() => {
            return of({ apiKey: true });
          })
        );
      } else {
        return of(null);
      }
    };
  }
}
